import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tab,
  Tabs,
  IconButton,
  Paper,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  EmojiEvents as EmojiEventsIcon,
  TrackChanges as TargetIcon,
  ExitToApp as LogoutIcon,
  Psychology as AIIcon,
  Send as SendIcon,
  Settings as SettingsIcon,
  Clear as ClearIcon,
  AutoAwesome as SparkleIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { useAuth, useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import UserService from '../services/userService';
import AIMentorService from '../services/aiMentorService';
import AvatarSelector from '../components/common/AvatarSelector';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [editGoalsOpen, setEditGoalsOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [newGoals, setNewGoals] = useState('');
  const [editProfilePicture, setEditProfilePicture] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editMobileOpen, setEditMobileOpen] = useState(false);
  const [newMobileNumber, setNewMobileNumber] = useState('');

  // AI Mentor States (simplified - no API key needed)
  const [chatMessages, setChatMessages] = useState<Array<{role: 'user' | 'assistant', content: string}>>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);

  const { user, logout } = useAuth();
  const { updateUserGoals, refreshUser } = useApp();

  // Load AI Mentor configuration on mount
  React.useEffect(() => {
    loadAIMentorConfig();
  }, []);

  // Check if mobile number is missing and show dialog
  React.useEffect(() => {
    if (user && !user.mobileNumber) {
      setEditMobileOpen(true);
    }
  }, [user]);

  const loadAIMentorConfig = async () => {
    try {
      setConfigLoading(true);
      const config = await AIMentorService.getConfig();

      // No API key required anymore - AI Mentor always available
      setChatMessages(config.chatHistory || []);
    } catch (error: any) {
      console.error('Failed to load AI Mentor config:', error);
      setError('Failed to load chat history');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleEditGoals = () => {
    setNewGoals(user?.goals || '');
    setEditGoalsOpen(true);
  };

  const handleSaveGoals = async () => {
    try {
      setError(null);
      await updateUserGoals(newGoals);
      setEditGoalsOpen(false);
      setSuccess('Goals updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError('Failed to update goals. Please try again.');
    }
  };

  const handleEditProfile = () => {
    setEditProfilePicture(user?.profilePicture || '');
    setError(null);
    setEditProfileOpen(true);
  };

  const handleSaveProfile = async () => {
    try {
      setError(null);
      await UserService.updateUserProfile({
        profilePicture: editProfilePicture,
      });

      if (refreshUser) {
        await refreshUser();
      }

      setEditProfileOpen(false);
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError('Failed to update profile. Please try again.');
    }
  };

  const handleSaveMobileNumber = async () => {
    if (!newMobileNumber.trim()) {
      setError('Mobile number is required');
      return;
    }

    if (!/^[0-9]{10}$/.test(newMobileNumber)) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    try {
      setError(null);
      await UserService.updateUserProfile({
        mobileNumber: newMobileNumber.trim(),
      });

      if (refreshUser) {
        await refreshUser();
      }

      setEditMobileOpen(false);
      setNewMobileNumber(''); // Reset the input
      setSuccess('Mobile number updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError('Failed to update mobile number. Please try again.');
    }
  };

  const handleLogout = () => {
    logout();
  };

  // API key validation removed - no longer needed with Azure OpenAI

  const handleSendMessage = async () => {
    if (!currentMessage.trim()) return;

    const userMessage = currentMessage.trim();
    setCurrentMessage('');

    // Optimistically add user message to UI
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsSending(true);

    try {
      const result = await AIMentorService.sendMessage(userMessage);

      if (result.success) {
        // Add AI response to UI
        setChatMessages(prev => [...prev, { role: 'assistant', content: result.response }]);
      } else {
        setError('Failed to get response from AI Mentor');
        // Remove optimistic user message on error
        setChatMessages(prev => prev.slice(0, -1));
      }
    } catch (error: any) {
      setError(error.message || 'Failed to send message. Please try again.');
      // Remove optimistic user message on error
      setChatMessages(prev => prev.slice(0, -1));
    } finally {
      setIsSending(false);
    }
  };

  const handleClearChat = async () => {
    try {
      const result = await AIMentorService.clearChatHistory();

      if (result.success) {
        setChatMessages([]);
        setSuccess('Chat history cleared successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.message || 'Failed to clear chat history');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to clear chat history. Please try again.');
    }
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          User not found. Please log in again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ p: 0, mx: 0, width: '100%' }}>
      <Box sx={{
        minHeight: '100vh',
        backgroundColor: { xs: '#f5f7fa', sm: '#f0f2f5' },
        pb: { xs: 10, sm: 4 }
      }}>
        {/* Header Banner */}
        <Box sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          pt: { xs: 3, sm: 6 },
          pb: { xs: 8, sm: 10 },
          px: { xs: 1.5, sm: 3 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.1,
          }
        }}>
          <Box sx={{
            maxWidth: 'lg',
            mx: 'auto',
            position: 'relative',
            zIndex: 1
          }}>
            <Typography
              variant="h3"
              sx={{
                color: 'white',
                fontWeight: 800,
                mb: 1,
                fontSize: { xs: '1.75rem', sm: '3rem' },
                textShadow: '0 2px 10px rgba(0,0,0,0.2)'
              }}
            >
              My Profile
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255,255,255,0.9)',
                fontSize: { xs: '0.9rem', sm: '1.25rem' }
              }}
            >
              Manage your account, goals, and AI Mentor
            </Typography>
          </Box>
        </Box>

        {/* Success Alert */}
        {success && (
          <Container maxWidth="lg" sx={{ mt: -6, position: 'relative', zIndex: 2, px: { xs: 1.5, sm: 3 } }}>
            <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
              {success}
            </Alert>
          </Container>
        )}

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ mt: { xs: -5, sm: -6 }, position: 'relative', zIndex: 1, px: { xs: 1.5, sm: 3 } }}>
          <Card sx={{
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            mb: 3
          }}>
            {/* Profile Header */}
            <Box sx={{
              background: 'linear-gradient(to right, #f8f9fa 0%, #e9ecef 100%)',
              p: { xs: 2, sm: 4 },
              borderBottom: '1px solid #dee2e6'
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: { xs: 'center', sm: 'flex-start' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 3 }
              }}>
                <Avatar
                  sx={{
                    width: { xs: 80, sm: 100 },
                    height: { xs: 80, sm: 100 },
                    fontSize: { xs: '2rem', sm: '2.5rem' },
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 8px 20px rgba(102, 126, 234, 0.4)',
                    border: '4px solid white'
                  }}
                >
                  {user.profilePicture || user.name?.charAt(0) || '👤'}
                </Avatar>
                <Box sx={{
                  flexGrow: 1,
                  textAlign: { xs: 'center', sm: 'left' }
                }}>
                  <Typography variant="h4" sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '1.5rem', sm: '2rem' }
                  }}>
                    {user.name}
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                    gap: 1,
                    mb: 1,
                    flexWrap: 'wrap'
                  }}>
                    <Chip
                      icon={user.role === 'Faculty' ? <SchoolIcon /> : <PersonIcon />}
                      label={user.role}
                      color={user.role === 'Faculty' ? 'secondary' : 'primary'}
                      sx={{ fontWeight: 600 }}
                    />
                    {user.isVerified && (
                      <Chip
                        label="✓ Verified"
                        color="success"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    {user.branch}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={handleEditProfile}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #6a4291 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(102, 126, 234, 0.4)',
                    },
                    transition: 'all 0.3s'
                  }}
                >
                  Edit Profile
                </Button>
              </Box>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 1, sm: 3 } }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    minHeight: { xs: 48, sm: 56 },
                    minWidth: { xs: 'auto', sm: 120 },
                    px: { xs: 1.5, sm: 2 }
                  }
                }}
              >
                <Tab icon={<PersonIcon />} label="Overview" iconPosition="start" />
                <Tab icon={<AIIcon />} label="AI Mentor" iconPosition="start" />
                <Tab icon={<SettingsIcon />} label="Settings" iconPosition="start" />
              </Tabs>
            </Box>

            {/* Tab Panels */}
            <TabPanel value={activeTab} index={0}>
              <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: 3 }}>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                  gap: 3
                }}>
                  {/* Left Column */}
                  <Box>
                    {/* Contact Info Card */}
                    <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Contact Information
                      </Typography>
                      <List>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <EmailIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Email"
                            secondary={user.email}
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <PhoneIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Mobile Number"
                            secondary={user.mobileNumber || 'Not provided'}
                          />
                          {(!user.mobileNumber || user.mobileNumber === '') && (
                            <IconButton size="small" onClick={() => setEditMobileOpen(true)} color="primary">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                        </ListItem>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <BusinessIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Branch"
                            secondary={user.branch}
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <GroupIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Clubs"
                            secondary={`Member of ${user.clubs?.length || 0} club${(user.clubs?.length || 0) !== 1 ? 's' : ''}`}
                          />
                        </ListItem>
                      </List>
                    </Paper>

                    {/* Goals Card */}
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TargetIcon color="primary" />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            My Goals
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={handleEditGoals}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      {user.goals ? (
                        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                          {user.goals}
                        </Typography>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                          <TargetIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                          <Typography variant="body2" sx={{ mb: 2 }}>
                            No goals set yet
                          </Typography>
                          <Button
                            variant="outlined"
                            onClick={handleEditGoals}
                            sx={{ textTransform: 'none' }}
                          >
                            Add Your Goals
                          </Button>
                        </Box>
                      )}
                    </Paper>
                  </Box>

                  {/* Right Column */}
                  <Box>
                    {/* Club Memberships */}
                    <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Club Memberships
                      </Typography>
                      {user.clubs && user.clubs.length > 0 ? (
                        <List dense>
                          {user.clubs.map((clubMembership: any, index: number) => (
                            <ListItem key={index} sx={{ px: 0, py: 1 }}>
                              <ListItemIcon>
                                <Avatar sx={{ width: 36, height: 36, fontSize: '1.1rem' }}>
                                  {clubMembership.club?.logo || '🏛️'}
                                </Avatar>
                              </ListItemIcon>
                              <ListItemText
                                primary={clubMembership.club?.name || 'Unknown Club'}
                                secondary={clubMembership.memberRole || 'Member'}
                              />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                          <GroupIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                          <Typography variant="body2">
                            Not a member of any clubs yet
                          </Typography>
                        </Box>
                      )}
                    </Paper>

                    {/* Badges */}
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Achievements
                      </Typography>
                      {user.badges && user.badges.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {user.badges.map((badge: any, index: number) => (
                            <Chip
                              key={index}
                              icon={<EmojiEventsIcon />}
                              label={badge.name || badge}
                              color="warning"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                          <EmojiEventsIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                          <Typography variant="body2">
                            No achievements yet
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Box>
                </Box>
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: 3 }}>
                {/* AI Mentor Card */}
                <Paper
                  elevation={0}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    p: 4,
                    borderRadius: 3,
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '-50%',
                      right: '-50%',
                      width: '200%',
                      height: '200%',
                      background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                      animation: 'pulse 4s ease-in-out infinite',
                    },
                    '@keyframes pulse': {
                      '0%, 100%': { transform: 'scale(1)', opacity: 0.5 },
                      '50%': { transform: 'scale(1.1)', opacity: 0.8 },
                    }
                  }}
                  onClick={() => navigate('/ai-mentor')}
                >
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{
                      display: 'inline-flex',
                      p: 2,
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.2)',
                      mb: 2,
                      backdropFilter: 'blur(10px)'
                    }}>
                      <AIIcon sx={{ fontSize: 48 }} />
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, letterSpacing: '0.5px' }}>
                      AI Study Mentor
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 3, opacity: 0.95 }}>
                      <SparkleIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Your Personal AI Study Assistant
                      </Typography>
                      <SparkleIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Typography variant="body1" sx={{ mb: 3, opacity: 0.95 }}>
                      Get instant help with your studies, assignments, and concepts
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<SparkleIcon />}
                      sx={{
                        background: 'white',
                        color: '#667eea',
                        fontWeight: 700,
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1rem',
                        '&:hover': {
                          background: 'rgba(255,255,255,0.95)',
                          transform: 'scale(1.05)',
                        }
                      }}
                    >
                      Open AI Mentor
                    </Button>
                  </Box>
                </Paper>

              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: 3 }}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Account Actions
                  </Typography>
                  <List>
                    <ListItem
                      component="div"
                      onClick={handleLogout}
                      sx={{
                        borderRadius: 2,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'error.lighter',
                          '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                            color: 'error.main'
                          }
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                      <ListItemIcon>
                        <LogoutIcon color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Sign Out"
                        slotProps={{
                          primary: { style: { fontWeight: 600 } }
                        }}
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Box>
            </TabPanel>
          </Card>
        </Container>

        {/* Edit Goals Dialog */}
        <Dialog open={editGoalsOpen} onClose={() => setEditGoalsOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Your Goals</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="What are your goals and aspirations?"
              value={newGoals}
              onChange={(e) => setNewGoals(e.target.value)}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditGoalsOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveGoals}>
              Save Goals
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Profile Dialog */}
        <Dialog open={editProfileOpen} onClose={() => setEditProfileOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Change Profile Avatar</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Box sx={{ mt: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    fontSize: '2rem',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  }}
                >
                  {editProfilePicture || user?.name?.charAt(0) || '👤'}
                </Avatar>
                <Typography variant="body2" color="text.secondary">
                  Selected Avatar
                </Typography>
              </Box>
              <AvatarSelector
                selectedAvatar={editProfilePicture}
                onSelect={(avatar) => setEditProfilePicture(avatar)}
              />
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              Name and branch cannot be changed. Please contact your administrator if you need to update this information.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditProfileOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveProfile}>
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Mobile Number Dialog */}
        <Dialog
          open={editMobileOpen}
          onClose={() => {
            setEditMobileOpen(false);
            setNewMobileNumber('');
            setError(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {!user?.mobileNumber ? 'Add Mobile Number' : 'Update Mobile Number'}
          </DialogTitle>
          <DialogContent>
            {!user?.mobileNumber && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Adding your mobile number helps with account security and communication. You can skip this for now and add it later.
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Mobile Number"
              placeholder="Enter 10-digit mobile number"
              value={newMobileNumber}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                if (value.length <= 10) {
                  setNewMobileNumber(value);
                }
              }}
              inputProps={{
                maxLength: 10,
                pattern: '[0-9]*',
              }}
              helperText="Please enter a valid 10-digit mobile number"
              sx={{ mt: 2 }}
              autoFocus
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setEditMobileOpen(false);
              setNewMobileNumber('');
              setError(null);
            }}>
              {!user?.mobileNumber ? 'Skip for Now' : 'Cancel'}
            </Button>
            <Button variant="contained" onClick={handleSaveMobileNumber}>
              {!user?.mobileNumber ? 'Submit' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default ProfilePage;
